// Form Analytics Utility for Jet Digital Website
// Tracks form interactions and performance metrics

class FormAnalytics {
  constructor() {
    this.events = [];
    this.sessionId = this.generateSessionId();
    this.startTime = Date.now();
  }

  generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Track form start event
  trackFormStart(formType, formLocation) {
    const event = {
      type: 'form_start',
      formType,
      formLocation,
      timestamp: Date.now(),
      sessionId: this.sessionId,
      userAgent: navigator.userAgent,
      screenSize: `${window.screen.width}x${window.screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`
    };

    this.events.push(event);
    this.logEvent(event);
  }

  // Track field interactions
  trackFieldInteraction(formType, fieldName, action, value = null) {
    const event = {
      type: 'field_interaction',
      formType,
      fieldName,
      action, // 'focus', 'blur', 'change', 'error'
      value: value ? value.length : null, // Track length, not actual value for privacy
      timestamp: Date.now(),
      sessionId: this.sessionId
    };

    this.events.push(event);
    this.logEvent(event);
  }

  // Track validation errors
  trackValidationError(formType, fieldName, errorMessage) {
    const event = {
      type: 'validation_error',
      formType,
      fieldName,
      errorMessage,
      timestamp: Date.now(),
      sessionId: this.sessionId
    };

    this.events.push(event);
    this.logEvent(event);
  }

  // Track form submission attempts
  trackSubmissionAttempt(formType, isValid, errors = []) {
    const event = {
      type: 'submission_attempt',
      formType,
      isValid,
      errorCount: errors.length,
      errors: errors.map(error => ({ field: error.field, type: error.type })),
      timestamp: Date.now(),
      sessionId: this.sessionId,
      timeToSubmit: Date.now() - this.startTime
    };

    this.events.push(event);
    this.logEvent(event);
  }

  // Track successful form submission
  trackSubmissionSuccess(formType, submissionData) {
    const event = {
      type: 'submission_success',
      formType,
      timestamp: Date.now(),
      sessionId: this.sessionId,
      timeToComplete: Date.now() - this.startTime,
      dataFields: Object.keys(submissionData),
      politicalPosition: submissionData.political_position || submissionData.politicalPosition,
      source: submissionData.source
    };

    this.events.push(event);
    this.logEvent(event);
    this.sendAnalytics();
  }

  // Track submission failures
  trackSubmissionFailure(formType, errorType, errorMessage) {
    const event = {
      type: 'submission_failure',
      formType,
      errorType, // 'network', 'server', 'timeout', 'validation'
      errorMessage,
      timestamp: Date.now(),
      sessionId: this.sessionId,
      timeToFailure: Date.now() - this.startTime
    };

    this.events.push(event);
    this.logEvent(event);
  }

  // Track WhatsApp integration usage
  trackWhatsAppClick(formType, formData) {
    const event = {
      type: 'whatsapp_click',
      formType,
      timestamp: Date.now(),
      sessionId: this.sessionId,
      hasFormData: Object.keys(formData).some(key => formData[key] && formData[key].trim()),
      completedFields: Object.keys(formData).filter(key => formData[key] && formData[key].trim()).length
    };

    this.events.push(event);
    this.logEvent(event);
  }

  // Track modal interactions
  trackModalInteraction(action, formType = 'client_onboarding') {
    const event = {
      type: 'modal_interaction',
      action, // 'open', 'close', 'backdrop_click'
      formType,
      timestamp: Date.now(),
      sessionId: this.sessionId
    };

    this.events.push(event);
    this.logEvent(event);
  }

  // Track performance metrics
  trackPerformanceMetric(metricType, value, formType) {
    const event = {
      type: 'performance_metric',
      metricType, // 'load_time', 'render_time', 'api_response_time'
      value,
      formType,
      timestamp: Date.now(),
      sessionId: this.sessionId
    };

    this.events.push(event);
    this.logEvent(event);
  }

  // Log event to console in development
  logEvent(event) {
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Form Analytics:', event);
    }
  }

  // Send analytics data to backend (implement as needed)
  async sendAnalytics() {
    try {
      // Only send in production or when explicitly enabled
      if (process.env.NODE_ENV === 'production' || process.env.REACT_APP_ENABLE_ANALYTICS === 'true') {
        const analyticsData = {
          sessionId: this.sessionId,
          events: this.events,
          summary: this.generateSummary()
        };

        // Send to analytics endpoint
        await fetch('/api/analytics/forms', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(analyticsData)
        });
      }
    } catch (error) {
      console.warn('Failed to send analytics:', error);
    }
  }

  // Generate session summary
  generateSummary() {
    const formStarts = this.events.filter(e => e.type === 'form_start');
    const submissions = this.events.filter(e => e.type === 'submission_success');
    const failures = this.events.filter(e => e.type === 'submission_failure');
    const validationErrors = this.events.filter(e => e.type === 'validation_error');

    return {
      sessionDuration: Date.now() - this.startTime,
      formsStarted: formStarts.length,
      formsCompleted: submissions.length,
      formsFailed: failures.length,
      validationErrors: validationErrors.length,
      conversionRate: formStarts.length > 0 ? (submissions.length / formStarts.length) * 100 : 0,
      averageCompletionTime: submissions.length > 0 
        ? submissions.reduce((sum, s) => sum + s.timeToComplete, 0) / submissions.length 
        : 0
    };
  }

  // Get current session data
  getSessionData() {
    return {
      sessionId: this.sessionId,
      events: this.events,
      summary: this.generateSummary()
    };
  }

  // Reset analytics for new session
  reset() {
    this.events = [];
    this.sessionId = this.generateSessionId();
    this.startTime = Date.now();
  }
}

// Create singleton instance
const formAnalytics = new FormAnalytics();

export default formAnalytics;

// Helper functions for easy integration
export const trackFormStart = (formType, formLocation) => {
  formAnalytics.trackFormStart(formType, formLocation);
};

export const trackFieldInteraction = (formType, fieldName, action, value) => {
  formAnalytics.trackFieldInteraction(formType, fieldName, action, value);
};

export const trackValidationError = (formType, fieldName, errorMessage) => {
  formAnalytics.trackValidationError(formType, fieldName, errorMessage);
};

export const trackSubmissionAttempt = (formType, isValid, errors) => {
  formAnalytics.trackSubmissionAttempt(formType, isValid, errors);
};

export const trackSubmissionSuccess = (formType, submissionData) => {
  formAnalytics.trackSubmissionSuccess(formType, submissionData);
};

export const trackSubmissionFailure = (formType, errorType, errorMessage) => {
  formAnalytics.trackSubmissionFailure(formType, errorType, errorMessage);
};

export const trackWhatsAppClick = (formType, formData) => {
  formAnalytics.trackWhatsAppClick(formType, formData);
};

export const trackModalInteraction = (action, formType) => {
  formAnalytics.trackModalInteraction(action, formType);
};

export const trackPerformanceMetric = (metricType, value, formType) => {
  formAnalytics.trackPerformanceMetric(metricType, value, formType);
};
